// Yuumi Bond VFX — symbolises Yuumi handing a cooldown point to the ally she's
// attached to. Plays in three phases:
//   1) Soft cyan pulse + motes converge at Yuumi.
//   2) Sparkles travel along a gentle bezier arc to the bonded ally.
//   3) A clock dial appears on the ally; its hand sweeps rapidly back, a "−1 CD"
//      label floats up.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const PULSE_MS  = 220;
const TRAVEL_MS = 480;
const DIAL_MS   = 620;
const TOTAL_MS  = PULSE_MS + TRAVEL_MS + DIAL_MS;
const MOTE_COUNT = 6;
const MOTE_WINDOW = 0.7;

const CYAN_BRIGHT = "#aef2ff";
const CYAN_GLOW   = "#44eeff";
const CYAN_SOFT   = "#80d8ff";

export class YuumiBondVFX implements Animation {
  private elapsed = 0;
  private readonly moteAngles: number[];

  constructor(
    private from: Point,
    private to: Point,
    private hexSize: number,
  ) {
    this.moteAngles = Array.from(
      { length: MOTE_COUNT },
      (_, i) => (i / MOTE_COUNT) * Math.PI * 2 + Math.random() * 0.4,
    );
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL_MS);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed;
    if (t < PULSE_MS) {
      this.drawPulse(ctx, t / PULSE_MS);
    } else if (t < PULSE_MS + TRAVEL_MS) {
      this.drawTravel(ctx, (t - PULSE_MS) / TRAVEL_MS);
    } else {
      this.drawDial(ctx, (t - PULSE_MS - TRAVEL_MS) / DIAL_MS);
    }
  }

  // Soft cyan pulse at Yuumi with motes drawing inward.
  private drawPulse(ctx: CanvasRenderingContext2D, p: number): void {
    const { from, hexSize } = this;
    const ease = Easing.easeOutQuad(p);

    ctx.save();
    ctx.globalAlpha = 0.35 * (1 - ease * 0.4);
    ctx.fillStyle = CYAN_GLOW;
    ctx.shadowColor = CYAN_GLOW;
    ctx.shadowBlur = 26;
    ctx.beginPath();
    ctx.arc(from.x, from.y, hexSize * (0.4 + 0.25 * ease), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    for (let i = 0; i < MOTE_COUNT; i++) {
      const angle = this.moteAngles[i];
      const r = hexSize * 0.95 * (1 - ease);
      const x = from.x + Math.cos(angle) * r;
      const y = from.y + Math.sin(angle) * r;
      this.drawSparkle(ctx, x, y, 2.5 + ease * 1.2, 0.4 + 0.5 * ease);
    }
  }

  // Sparkles arc from Yuumi to the bonded ally along a soft bezier.
  private drawTravel(ctx: CanvasRenderingContext2D, p: number): void {
    const { from, to, hexSize } = this;
    const cx = (from.x + to.x) / 2;
    const cy = (from.y + to.y) / 2 - hexSize * 1.6;

    // Faint trailing line for connection
    ctx.save();
    ctx.globalAlpha = 0.18 * (1 - Math.abs(p - 0.5) * 1.4);
    ctx.strokeStyle = CYAN_GLOW;
    ctx.shadowColor = CYAN_GLOW;
    ctx.shadowBlur = 10;
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.quadraticCurveTo(cx, cy, to.x, to.y);
    ctx.stroke();
    ctx.restore();

    for (let i = 0; i < MOTE_COUNT; i++) {
      const startP = (i / MOTE_COUNT) * (1 - MOTE_WINDOW);
      const endP = startP + MOTE_WINDOW;
      if (p < startP || p > endP + 0.06) continue;

      const localP = Math.min((p - startP) / MOTE_WINDOW, 1);
      const eased = Easing.easeInOutQuad(localP);
      const u = 1 - eased;
      const bx = u * u * from.x + 2 * u * eased * cx + eased * eased * to.x;
      const by = u * u * from.y + 2 * u * eased * cy + eased * eased * to.y;

      const alpha = Math.sin(Math.PI * localP) * 0.85;
      const size = 3.4 + 1.6 * Math.sin(Math.PI * localP);
      this.drawSparkle(ctx, bx, by, size, alpha);
    }
  }

  // Clock dial appears on the ally; hand sweeps backward rapidly. "−1 CD" rises.
  private drawDial(ctx: CanvasRenderingContext2D, p: number): void {
    const { to, hexSize } = this;
    const radius = hexSize * 0.55;
    const ease = Easing.easeOutCubic(p);

    // Outer ring
    ctx.save();
    const ringAlpha = (1 - p) * 0.8;
    ctx.globalAlpha = ringAlpha;
    ctx.strokeStyle = CYAN_BRIGHT;
    ctx.shadowColor = CYAN_GLOW;
    ctx.shadowBlur = 18;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(to.x, to.y, radius * (0.7 + 0.3 * ease), 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();

    // Tick marks (12, 3, 6, 9 — clock face)
    ctx.save();
    ctx.globalAlpha = ringAlpha * 0.9;
    ctx.strokeStyle = CYAN_BRIGHT;
    ctx.lineWidth = 2;
    ctx.shadowColor = CYAN_GLOW;
    ctx.shadowBlur = 8;
    const r = radius * (0.7 + 0.3 * ease);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 - Math.PI / 2;
      const x1 = to.x + Math.cos(a) * (r - 4);
      const y1 = to.y + Math.sin(a) * (r - 4);
      const x2 = to.x + Math.cos(a) * r;
      const y2 = to.y + Math.sin(a) * r;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }
    ctx.restore();

    // Sweeping hand — rotates COUNTER-clockwise (cooldown going down) ~270° fast.
    const handProgress = Math.min(p / 0.55, 1);
    const handEase = Easing.easeOutCubic(handProgress);
    const startAngle = -Math.PI / 2;
    const sweep = handEase * Math.PI * 1.5; // ~270°
    const angle = startAngle - sweep; // counter-clockwise

    ctx.save();
    ctx.globalAlpha = (1 - p) * 0.95;
    ctx.strokeStyle = CYAN_BRIGHT;
    ctx.shadowColor = CYAN_GLOW;
    ctx.shadowBlur = 14;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(
      to.x + Math.cos(angle) * (r - 5),
      to.y + Math.sin(angle) * (r - 5),
    );
    ctx.stroke();

    // Pivot dot
    ctx.fillStyle = CYAN_BRIGHT;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(to.x, to.y, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // "−1 CD" label rising and fading
    const labelProgress = p;
    const labelAlpha = labelProgress < 0.15
      ? labelProgress / 0.15
      : Math.max(0, 1 - (labelProgress - 0.4) / 0.6);
    if (labelAlpha > 0) {
      const liftY = to.y - radius - 8 - Easing.easeOutQuad(labelProgress) * hexSize * 0.55;
      ctx.save();
      ctx.globalAlpha = labelAlpha;
      ctx.font = "bold 13px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(0, 30, 50, 0.85)";
      ctx.fillStyle = CYAN_BRIGHT;
      ctx.shadowColor = CYAN_GLOW;
      ctx.shadowBlur = 10;
      const text = "−1 CD";
      ctx.strokeText(text, to.x, liftY);
      ctx.fillText(text, to.x, liftY);
      ctx.restore();
    }

    // Soft inner pulse to lift the ally visually
    if (p < 0.5) {
      const pulseP = p / 0.5;
      ctx.save();
      ctx.globalAlpha = (1 - pulseP) * 0.4;
      ctx.fillStyle = CYAN_GLOW;
      ctx.shadowColor = CYAN_GLOW;
      ctx.shadowBlur = 22;
      ctx.beginPath();
      ctx.arc(to.x, to.y, radius * (0.4 + pulseP * 0.55), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  private drawSparkle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    alpha: number,
  ): void {
    if (alpha <= 0) return;
    ctx.save();
    ctx.globalAlpha = alpha;

    // Outer glow
    ctx.shadowColor = CYAN_GLOW;
    ctx.shadowBlur = 12;
    ctx.fillStyle = CYAN_SOFT;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();

    // White core
    ctx.shadowBlur = 6;
    ctx.fillStyle = "#ffffff";
    ctx.globalAlpha = alpha * 0.9;
    ctx.beginPath();
    ctx.arc(x, y, size * 0.45, 0, Math.PI * 2);
    ctx.fill();

    // Cross-shaped sparkle rays
    ctx.globalAlpha = alpha * 0.7;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.moveTo(x - size * 1.6, y);
    ctx.lineTo(x + size * 1.6, y);
    ctx.moveTo(x, y - size * 1.6);
    ctx.lineTo(x, y + size * 1.6);
    ctx.stroke();
    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL_MS;
  }
}
